import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import axios from "axios";
import { useTranslation, Trans } from "react-i18next";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import ThemeToggle from "@/components/ThemeToggle";
import LandingChatWidget from "@/components/LandingChatWidget";
import PayrollCoverageMap from "@/components/PayrollCoverageMap";
import WalkthroughPlayer from "@/components/landing/WalkthroughPlayer";
import LiveDemoSection from "@/components/landing/LiveDemoSection";
import ProblemSolvers from "@/components/landing/ProblemSolvers";
import GrowthSections from "@/components/landing/GrowthSections";
import {
  Check, ArrowRight, Zap, ShieldCheck, ClipboardList, LineChart, Users,
  QrCode, Network, Workflow, Trophy, TrendingUp, Lock, MailCheck, Sparkles,
  UserCheck, User, ChevronDown, Store, Gift, Crown, Medal,
  Wrench, Package, Boxes, Diamond, PiggyBank,
  Car, Gauge, ShieldAlert, CreditCard, Calculator, Coins, Route, BadgePercent,
  FileText, BellRing, Download, Globe, Wallet, Landmark, Banknote,
  MapPin, Droplet, Fuel, Camera, Timer, Signal,
  MessageCircle, Calendar, DollarSign, Receipt, Info, Cpu, Briefcase,
  BookOpen, Bot, Navigation, Flame, Map, ClipboardCheck,
} from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1601929862217-f1bf94503333?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBjYXIlMjBkYXJrfGVufDB8fHx8MTc4MzQ3NTg2Mnww&ixlib=rb-4.1.0&q=85";
const BENTO_IMG = "https://images.unsplash.com/photo-1605437241278-c1806d14a4d9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwyfHxhdXRvJTIwZGV0YWlsaW5nfGVufDB8fHx8MTc4MzQ3NTg2Mnww&ixlib=rb-4.1.0&q=85";
const NEW_SURVIVAL_IMG = "https://static.prod-images.emergentagent.com/jobs/9d97f549-87f5-438f-b8c7-42e2466abf1f/images/78dcd874c3561e56d111265089f48fe365a53df6a527d4c88d6acb49f4db9317.jpeg";
const NEW_AVATAR_IMG = "https://static.prod-images.emergentagent.com/jobs/9d97f549-87f5-438f-b8c7-42e2466abf1f/images/2ae6e78ba1b4879c0f9bd2b1f7eb49e7a463079acef3dc0de7d558f1e827b487.jpeg";
const NEW_ACADEMY_IMG = "https://static.prod-images.emergentagent.com/jobs/9d97f549-87f5-438f-b8c7-42e2466abf1f/images/5a4d8dac8d870b5ffcccc13ec465f881949087ea6eb7bcd65aed463c1397d756.jpeg";

export default function Landing() {
  const nav = useNavigate();
  const { t } = useTranslation();
  const [leaderboard, setLeaderboard] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [billingCycle, setBillingCycle] = useState("monthly");   // monthly | yearly

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/public/leaderboard`)
      .then(r => setLeaderboard(r.data))
      .catch(() => setLeaderboard(null));
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/billing/pricing`)
      .then(r => setPricing(r.data))
      .catch(() => setPricing(null));
  }, []);

  // Fallback pricing (matches backend defaults) so the page renders instantly
  // even before the fetch completes.
  const P = pricing || {
    starter:  { monthly: 8.99,  yearly: 75.52,  yearly_monthly_effective: 6.29 },
    pro:      { monthly: 29.99, yearly: 251.92, yearly_monthly_effective: 20.99 },
    premium:  { monthly: 59.99, yearly: 503.92, yearly_monthly_effective: 41.99 },
    platinum: { monthly: 99.00, yearly: 831.60, yearly_monthly_effective: 69.30 },
    annual_discount_pct: 30,
  };
  const isYearly = billingCycle === "yearly";
  const priceOf = (tier) => (isYearly ? P[tier].yearly : P[tier].monthly);
  const suffix = isYearly ? "/yr" : "/mo";
  const yearlySavings = (tier) => (P[tier].monthly * 12) - P[tier].yearly;
  const secondaryLine = (tier) => isYearly
    ? `${t("landing.billed_annually_prefix", "Just")} $${P[tier].yearly_monthly_effective.toFixed(2)}${t("landing.billed_annually_suffix", "/mo billed annually")}`
    : t("landing.save_per_year", { amount: yearlySavings(tier).toFixed(2) });

  return (
    <div className="min-h-screen bg-[var(--ld-bg)] text-white antialiased">
      {/* NAV */}
      <div className="border-b border-white/5 sticky top-0 z-40 backdrop-blur-lg bg-[var(--ld-bg)]/85">
        <div className="max-w-7xl mx-auto px-3 md:px-6 h-14 md:h-16 flex items-center justify-between gap-2">
          <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="brand-link">
            <div className="w-8 h-8 rounded-md bg-[var(--ld-royal)] flex items-center justify-center font-heading font-bold">L</div>
            <div className="font-heading text-sm whitespace-nowrap">Legacy Detailing <span className="text-slate-500 hidden sm:inline">IO</span></div>
          </Link>
          <div className="md-only-flex items-center gap-6 text-xs text-slate-400">
            <a href="#live-demo" className="hover:text-white transition-colors" data-testid="nav-live-demo">Live demo</a>
            <a href="#walkthrough" className="hover:text-white transition-colors" data-testid="nav-walkthrough">Walkthrough</a>
            <a href="#solutions" className="hover:text-white transition-colors" data-testid="nav-solutions">Solutions</a>
            <a href="#whats-new" className="hover:text-white transition-colors" data-testid="nav-whats-new">What's new</a>
            <a href="#academy" className="hover:text-white transition-colors" data-testid="nav-academy">Academy</a>
            <a href="#features" className="hover:text-white transition-colors">{t("nav.features")}</a>
            <a href="#accounting" className="hover:text-white transition-colors" data-testid="nav-accounting">Accounting</a>
            <a href="#leaderboard" className="hover:text-white transition-colors">{t("nav.leaderboard")}</a>
            <a href="#pricing" className="hover:text-white transition-colors">{t("nav.pricing")}</a>
            <a href="#faq" className="hover:text-white transition-colors">{t("nav.faq")}</a>
          </div>
          <div className="flex items-center gap-1.5 md:gap-3 shrink-0">
            <ThemeToggle />
            <LanguageSwitcher variant="nav" />
            <Link to="/pricing" className="ld-btn ld-btn-ghost hidden md:inline-flex" data-testid="nav-pricing">{t("nav.pricing")}</Link>
            <Link to="/login" className="ld-btn ld-btn-ghost text-xs md:text-sm px-2 md:px-3" data-testid="nav-login">{t("nav.login")}</Link>
            <Link to="/register" className="ld-btn ld-btn-primary text-xs md:text-sm px-2.5 md:px-3 whitespace-nowrap" data-testid="nav-signup">
              <span className="md:hidden">Start free</span>
              <span className="hidden md:inline">{t("nav.start_trial")}</span>
            </Link>
          </div>
        </div>
      </div>

      {/* HERO */}
      <section className="hero-grad">
        <div className="max-w-7xl mx-auto px-4 md:px-6 pt-12 md:pt-24 pb-14 md:pb-20 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <span className="pill-royal">{t("landing.hero_pill")}</span>
            <div className="mt-3 text-[11px] uppercase tracking-widest text-cyan-300/80" data-testid="hero-os-pill">
              All-in-One Detailing Business Operating System
            </div>
            <h1 className="mt-3 font-heading font-bold text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight">
              {t("landing.hero_title_line1")}<br />
              <span className="text-[var(--ld-royal-hover)]">{t("landing.hero_title_line2")}</span>
            </h1>
            <p className="mt-6 text-lg text-slate-400 max-w-xl leading-relaxed">
              <Trans i18nKey="landing.hero_body"
                components={{
                  bold: <strong className="text-white" />,
                  yellow: <span className="text-yellow-400" />,
                  emerald: <span className="text-emerald-400" />,
                }}
              />
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <button onClick={() => nav("/register")} className="ld-btn ld-btn-primary text-base" data-testid="hero-cta">
                {t("landing.start_free")} <ArrowRight size={16} />
              </button>
              <a href="#walkthrough" className="ld-btn ld-btn-ghost inline-flex items-center gap-2" data-testid="hero-watch-btn">
                <span className="w-6 h-6 rounded-full bg-white text-black flex items-center justify-center">
                  <svg width="9" height="9" viewBox="0 0 10 10" fill="currentColor"><path d="M2 1l7 4-7 4V1z"/></svg>
                </span>
                Watch the walkthrough
              </a>
            </div>
            <div className="mt-5 flex flex-wrap gap-1.5" data-testid="hero-new-badges">
              {["Instant photo quotes", "Heat-safe scheduling", "50-state payroll", "AI receipt scanning", "Stripe pay-now invoices"].map((b) => (
                <span key={b} className="rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider bg-white/5 border border-white/10 text-slate-300">
                  {b}
                </span>
              ))}
            </div>
            <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4 text-xs text-slate-500">
              {[
                ["90+", t("landing.stat_modules")],
                ["5", t("landing.stat_revenue_streams")],
                ["5-tier", t("landing.stat_commissions")],
                ["EN/ES", t("landing.stat_free")],
              ].map(([n, l]) => (
                <div key={l}>
                  <div className="font-heading text-2xl font-bold text-white tabular-nums">{n}</div>
                  <div className="mt-1 uppercase tracking-widest">{l}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="ld-card overflow-hidden group">
              <div className="relative">
                <img src={HERO_IMG} alt="Detailed black BMW M3" className="w-full h-72 object-cover group-hover:scale-[1.02] transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-[var(--ld-bg)] via-transparent" />
              </div>
              <div className="p-5 border-t border-white/10">
                <div className="text-[10px] tracking-widest text-slate-500 uppercase mb-3">{t("landing.live_dashboard_pill")}</div>
                <div className="grid grid-cols-3 gap-3">
                  <Stat label={t("landing.live_net_profit")} value="$4,281" tone="green" />
                  <Stat label={t("landing.live_reps_active")} value="7" tone="silver" />
                  <Stat label={t("landing.live_mtd_sales")} value="$28.4k" tone="blue" />
                </div>
                <div className="mt-4 pt-4 border-t border-white/5 flex items-center justify-between text-xs">
                  <span className="text-slate-400">{t("landing.live_top_rep")}</span>
                  <span className="text-white flex items-center gap-1.5"><Trophy size={12} className="text-[var(--ld-warn)]" /> Marcus S. · $3,120</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* LIVE DEMO — one-click seeded personas (owner / rep / customer) */}
      <LiveDemoSection />

      {/* VIDEO WALKTHROUGH — animated product tour of the 4 heaviest flows */}
      <section id="walkthrough" className="border-t border-white/5 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-24">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <span className="uppercase text-xs tracking-[0.2em] font-bold text-cyan-300/80" data-testid="walkthrough-eyebrow">
              90-second walkthrough
            </span>
            <h2 className="mt-3 font-heading text-4xl md:text-5xl font-black tracking-tight">
              Watch it run a <span className="text-[var(--ld-royal-hover)]">real day</span>.
            </h2>
            <p className="mt-3 text-slate-400 leading-relaxed">
              Quote from photos, dodge the heat, run payroll, scan the receipts, certify your rep army — the five flows detailers use every single day.
            </p>
          </div>
          <WalkthroughPlayer />
        </div>
      </section>

      {/* PROBLEM-SOLVER BENTO — heavy-use features, framed by pain */}
      <ProblemSolvers />

      {/* SALES REP PROGRAM + CUSTOMER REWARDS */}
      <GrowthSections />

      {/* ============================================================
           EXPANDED OS SECTIONS (Feb 2026)
           Connected-workflow strip → Accounting → Automation → OS Hub.
           Purely additive; existing branding/design language preserved.
           ============================================================ */}

      {/* 1) CONNECTED WORKFLOW STRIP — PRD §2 */}
      <ConnectedWorkflowStrip />

      {/* FIELD-FIRST STRIP — new since v2.70 (mobile ops + IRS mileage) */}
      <section className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-14 md:py-20">
          <div className="flex items-baseline justify-between flex-wrap gap-3 mb-8">
            <div>
              <span className="text-xs tracking-widest text-emerald-400/80 uppercase">NEW · Feb 2026</span>
              <h2 className="mt-2 font-heading text-3xl md:text-4xl font-bold tracking-tight">
                Field-first — because <span className="text-[var(--ld-royal-hover)]">detailers don't sit at desks</span>.
              </h2>
            </div>
            <p className="text-slate-400 max-w-md text-sm leading-relaxed">
              Five new modules built after riding along on 100+ mobile jobs. Every one runs from your phone in the customer's driveway.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-3">
            <FieldTile
              icon={MapPin}
              tone="royal"
              title="Field Tab"
              body="GPS-pinned jobs, before/after photos, offline queue, route ordering, and an End-of-Day SMS summary to the owner. Built for the driveway."
            />
            <FieldTile
              icon={Droplet}
              tone="cyan"
              title="DI Resin Intelligence"
              body="Every job's PPM × gallons drains a live tank gauge. Sidebar bell fires when resin is low. Water cost auto-flows into P&L per job."
            />
            <FieldTile
              icon={Gauge}
              tone="emerald"
              title="Start / End Odometer"
              body="Tap Start in the morning, tap End at night. AI reads the number off your dashboard photo. Auto-writes to the IRS mileage log."
            />
            <FieldTile
              icon={Fuel}
              tone="amber"
              title="Fuel Receipt Scanner"
              body="Snap a gas receipt. GPT-5 Vision extracts gallons, price/gal, station, and date. Rolling actual cost/mile keeps your job P&L honest."
            />
            <FieldTile
              icon={LineChart}
              tone="violet"
              title="90-Day Cost/Mile Trend"
              body="Stacked chart of fuel + maintenance + depreciation per vehicle. Spot the truck that's silently killing your margins before you lose the month."
            />
          </div>

          {/* Sub-callouts — micro features that reinforce the field-first thesis */}
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3">
            <MiniCallout icon={Camera} label="Photo-required IRS audit trail" />
            <MiniCallout icon={Timer} label="Travel vs work timer per job" />
            <MiniCallout icon={Signal} label="Offline queue with auto-sync" />
            <MiniCallout icon={Route} label="Personal vs business mile splitting" />
          </div>
        </div>
      </section>

      {/* FEATURE STRIP — differentiator callout */}
      <section id="features" className="max-w-7xl mx-auto px-6 pt-24 pb-12">
        <div className="mb-14 max-w-3xl">
          <span className="text-xs tracking-widest text-slate-500 uppercase">{t("landing.features_eyebrow")}</span>
          <h2 className="mt-2 font-heading text-4xl md:text-5xl font-bold tracking-tight">
            {t("landing.features_title_line1")}<br />
            <span className="text-[var(--ld-royal-hover)]">{t("landing.features_title_line2")}</span>
          </h2>
          <p className="mt-4 text-slate-400 leading-relaxed">
            {t("landing.features_body")}
          </p>
        </div>

        {/* What's new — Jun 2026 ribbon */}
        <div className="mb-8 rounded-2xl border border-cyan-500/25 bg-gradient-to-br from-cyan-500/10 via-slate-900/40 to-orange-500/5 p-5" data-testid="landing-whats-new">
          <div className="flex items-center gap-2 mb-3">
            <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] uppercase tracking-widest bg-cyan-500/15 text-cyan-200 border border-cyan-500/30">
              <Sparkles size={12} /> Shipped · Jun 2026
            </span>
            <span className="text-xs text-slate-400">Fresh off the line — one line each</span>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-2 text-[13px]">
            <span className="flex items-start gap-2 text-slate-300"><ShieldAlert size={14} className="text-orange-300 mt-0.5 shrink-0"/> Cash-Flow Survival Mode — 4 levels from Healthy to Critical</span>
            <span className="flex items-start gap-2 text-slate-300"><Calculator size={14} className="text-cyan-300 mt-0.5 shrink-0"/> Bulk-deal guard — know if a "deal" drops you a level before buying</span>
            <span className="flex items-start gap-2 text-slate-300"><ClipboardCheck size={14} className="text-emerald-300 mt-0.5 shrink-0"/> Per-job shopping lists + Run Survival Plan checklist</span>
            <span className="flex items-start gap-2 text-slate-300"><Bot size={14} className="text-cyan-300 mt-0.5 shrink-0"/> AI videos starring YOUR avatar — upload 3 photos, Veo 3.1 does the rest</span>
            <span className="flex items-start gap-2 text-slate-300"><Camera size={14} className="text-cyan-300 mt-0.5 shrink-0"/> 8-second avatar likeness preview before full renders</span>
            <span className="flex items-start gap-2 text-slate-300"><Download size={14} className="text-cyan-300 mt-0.5 shrink-0"/> Auto render queue + one-tap MP4 downloads</span>
            <span className="flex items-start gap-2 text-slate-300"><BookOpen size={14} className="text-cyan-300 mt-0.5 shrink-0"/> Academy slideshows, multi-page lessons + AI slide decks</span>
            <span className="flex items-start gap-2 text-slate-300"><Coins size={14} className="text-emerald-300 mt-0.5 shrink-0"/> AI credit wallet — Stripe top-ups, auto-recharge, monthly statements</span>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* ---------- FRESH — FEB 2026 ---------- */}
          <Feature icon={Navigation} title="TomTom Dispatch & Routing" body="Drop every job on a live TomTom map, then hit Optimize — waypoint sequencing runs up to 12 stops per route, computes total miles + drive-time, and stamps a per-stop ETA. Auto-recalculates the moment a job is added, deleted, or rescheduled, so the board and the Field Tab never go stale." tag="All plans · NEW" />
          <Feature icon={MapPin} title="Rep Field Route (Mobile ETA)" body="Each rep opens their phone and sees only their own stops in optimized order, each with a live ETA and turn-by-turn hand-off. No more group texts of the day's plan — every rep is on the exact same page as dispatch, and every reshuffle pushes to their phone instantly." tag="Premium · NEW" />
          <Feature icon={Route} title="Rep Drop-Off Routing (Door-to-Door)" body="Upload a CSV of storefronts or residential targets → we geocode every address, cluster by density, and sequence 12-stop day batches with TomTom optimization. A 60-lead push turns into 5 sequential day routes without a whiteboard." tag="Premium · NEW" />
          <Feature icon={Map} title="Rep Progress Map" body="Every dropped card renders as a color-coded pin on the manager's TomTom map — pending gray, done green, skipped amber. Coverage, gaps, and hot corridors are visible at a glance without pulling reports." tag="Premium · NEW" />
          <Feature icon={Flame} title="Business-Type Hot Territory Map" body="Doesn't just count drops — tracks which business types ACTUALLY convert (barbershop, cafe, auto shop, salon…). Business types with ≥2 QR redemptions AND ≥25% redemption rate get flagged HOT and future leads of that type light up so reps double down on what's paying." tag="Premium · NEW" />
          <Feature icon={QrCode} title="Custom QR Landing Pages" body="Every drop-off card carries a unique QR. Pick your redemption behavior per-tenant — a hosted branded mini page with your logo & promo code, a redirect to your own site with the promo (and QR token) appended, or silent scan logging. Every scan feeds the hot-territory heatmap." tag="Premium · NEW" />
          <Feature icon={Calculator} title="Quick Price Guide Wizard" body="Mobile-friendly quoting in seconds. Auto-pulls from Service Pricing and generates dynamic ranges for compact / sedan / SUV / truck. Live job cost + margin sits next to the quote so nobody quotes at a loss on the driveway." tag="All plans · NEW" />
          <Feature icon={ClipboardCheck} title="Proof-of-Drop with Photo + GPS" body="Every completed drop-off captures the rep's GPS and an optional proof photo (via Emergent Object Storage). Ends the 'did the rep actually go there?' debate and locks the drop into the territory analytics." tag="Premium · NEW" />

          <Feature icon={Network} title="Multi-Tier Commissions" body="Reps recruit sub-reps. Recruiter earns a % override on their downline, cascading up to 5 tiers deep. Set the rate once, the engine does the rest." tag="Premium" />
          <Feature icon={UserCheck} title="Isolated Rep Portal" body="Every rep gets their own login at /rep. KPI cards, bonus progress bars, awards feed — never sees your business data." tag="Premium" />
          <Feature icon={Workflow} title="Approval Queue" body="Reps log bookings, D2D, or social sales from their phone. Owner reviews and approves. Commission compute + multi-tier cascade fires automatically." tag="Premium" />
          <Feature icon={Store} title="Store ROI Intelligence" body="Anonymized, org-wide 'which store type converts best'. Reps see 'target barbershops first — $18.57/card' without ever seeing your customers." tag="Premium" />
          <Feature icon={QrCode} title="QR-Tracked Referrals" body="Every rep gets a personal QR code. Customer scans → sale auto-routes to the right rep + cascades overrides up the chain." tag="Premium" />
          <Feature icon={Gift} title="Software Referral Program" body="Refer another detailer → earn 15% of their subscription monthly. Reps sell for a cut, cascading passive income up to 5 tiers deep. Recurring for as long as they stay." tag="Premium" />

          <Feature icon={Wrench} title="Tool Rental Pro" body="18-module rental business inside your account. Tool Catalog, Package Builder, Rental Orders, deposit tracking — a whole second revenue stream you already have inventory for." tag="Platinum" />
          <Feature icon={Trophy} title="Legacy Trust Score™" body="Automated customer scoring from behavior. Bronze/Silver/Gold tiers unlock deposit discounts. Late returners auto-flagged. Reduces friction without exposing you to risk." tag="Platinum" />
          <Feature icon={PiggyBank} title="Rent-to-Own Engine" body="40% of every rental payment auto-credits toward purchase. Eligibility checker, PMT amortization, and a live agreement tracker built in." tag="Platinum" />
          <Feature icon={Boxes} title="Capital Allocation + NPV" body="Rank every upgrade decision by NPV, ROI, and payback period. Stop guessing which purchase pays back — the engine tells you." tag="Platinum" />
          <Feature icon={LineChart} title="Business Valuation" body="Live valuation math (annual net × configurable multiplier). Know what your business is worth today — for lending, partnering, or exit conversations." tag="Platinum" />

          <Feature icon={Car} title="Auto Maintenance Module" body="Track every service vehicle. Editable maintenance schedule, work log with linked services, curved depreciation, and rolling 10,000-mile reserve fund — automatically." tag="Platinum · NEW" />
          <Feature icon={Gauge} title="True Cost-per-Mile" body="Repair + depreciation math combined into a live $/mi figure. One-tap injects into Local Routes, Cross-City Planner, and Quick Quote so drive fees stop leaking profit." tag="Platinum · NEW" />
          <Feature icon={Route} title="Rolling 10k-Mile Reserve" body="Shows exactly how much you should be setting aside every month for the next big repair — window shifts with your odometer so the number is always fresh." tag="Platinum · NEW" />
          <Feature icon={Calculator} title="Job Profit + Risk Calculator" body="Live math on every quote. Down payment vs. financing fees, hard costs, net profit — flags jobs that look profitable but leave you carrying too much risk. Embedded in 3 places." tag="Premium · NEW" />
          <Feature icon={Coins} title="Financing Origination Commissions" body="A new commission stream. When a rep gets a customer approved on 3/6/12-month financing, they earn — tiered by loan size, configurable, and cascades up the 5-tier chain." tag="Premium · NEW" />
          <Feature icon={CreditCard} title="Financing Command Center" body="6 KPI cards + 4 charts (cashflow, status donut, plan usage, top clients). Overdue alerts panel + originator dashboard — your whole AR portfolio at a glance." tag="Pro & up · NEW" />
          <Feature icon={BadgePercent} title="Rolling-Window Rewards" body="Tiers no longer 'stick' at Platinum forever. Silver/Gold/Platinum recomputes on every job from a configurable 3-month spend window — customers move down when they stop coming in, so rewards keep working." tag="Pro & up · NEW" />
          <Feature icon={FileText} title="Client Invoice System" body="Turn any completed job into a professional invoice in one click. Email or SMS the customer, they pay with Stripe on a branded public page, or download a print-ready PDF. Overdue reminders auto-fire on your schedule." tag="Pro & up · NEW" />
          <Feature icon={BellRing} title="Overdue Auto-Reminders" body="Set your cadence (default 3/7/14 days after due date) and the system nudges customers on autopilot — same channel as the original send, capped by your max, with a 24-hour de-dupe so nothing goes out twice." tag="Pro & up · NEW" />

          {/* ---------- FIELD-FIRST (v2.70+) ---------- */}
          <Feature icon={MapPin} title="Field Tab (Mobile Ops)" body="A mobile dispatch view built for the driveway. GPS pins, before/after photos, offline queue, route ordering by proximity, drive-vs-work timers on every job, and an End-of-Day SMS summary to the owner. Everything a detailer does on the road, done from their phone." tag="All plans · NEW" />
          <Feature icon={Droplet} title="DI Resin Intelligence" body="Track your DI tank by PPM × gallons — not flat gallons. Live capacity gauge, sidebar alerts before resin exhaustion, per-job water cost, and customer-water FREE toggle so detailers never eat a cost they didn't incur." tag="All plans · NEW" />
          <Feature icon={Gauge} title="IRS Mileage — Start / End Odometer" body="Tap Start in the morning, tap End at night. GPT-5 Vision reads the odometer number straight off the dashboard photo. Auto-cascades into your IRS Year-End Summary as a tax credit on every day's P&L — with photo audit trail baked in." tag="All plans · NEW" />
          <Feature icon={Fuel} title="Fuel Receipt Scanner + Rolling $/Mile" body="Snap a gas receipt at the pump. GPT-5 Vision extracts gallons + price/gal + station + date. Rolling actual cost/mile (30-day window) becomes the new primary source for job P&L — no more flat-rate guesses." tag="All plans · NEW" />
          <Feature icon={LineChart} title="90-Day Cost/Mile Trend" body="Stacked area chart per fleet vehicle: fuel + maintenance + depreciation. A 4-week-vs-prior-4-week trend chip turns rose when costs are climbing — so you catch the truck killing your margins before the month ends, not after." tag="Platinum · NEW" />
          <Feature icon={Camera} title="Fleet Management + Assignment" body="Add unlimited trucks, vans, and personal cars. Assign each vehicle to a specific worker or leave it in the shared pool. Each detailer picks their car from their phone. Cost-per-mile follows the vehicle, not the driver." tag="All plans · NEW" />

          {/* ---------- PERSONAL FINANCE WORKSPACE (Pro-tier, NEW) ---------- */}
          <Feature icon={Wallet} title="Personal Finance Workspace" body="A full 4th workspace inside your Legacy IO account. Household budget, bills tracker, net worth ledger, debt payoff engine and savings goals — the exact system every 'run your money like a business' guru sells for $497. Included with Pro." tag="Pro · NEW" />
          <Feature icon={Landmark} title="Avalanche Debt Payoff Engine" body="Enter every card, loan, and BNPL. The engine picks the highest-APR debt first, rolls freed-up minimums into the next one, and shows you the exact month you'll be debt-free. See how much interest you save vs. the minimums-only trap." tag="Pro · NEW" />
          <Feature icon={PiggyBank} title="Emergency Fund + Goals" body="Auto-computes your emergency fund target from your essentials budget × months of coverage. Then unlimited savings goals with auto-calc completion dates. Because you can't scale a business while personal money is on fire." tag="Pro · NEW" />
          <Feature icon={Banknote} title="Plaid Auto-Sync (Coming Soon)" body="Every manual transaction lives in a Plaid-compatible schema, so the day we flip the switch, your bank feed goes live instantly — with zero re-entry. Auto-categorization + a review queue you approve from your phone." tag="Pro · roadmap" />
          <Feature icon={Wallet} title="50/30/20 Monthly Budget" body="Every category, budgeted vs. actual, live from your Daily Log. Status chips flag every category as On track / Watch / Over so you catch overspending BEFORE the credit card statement hits." tag="Pro · NEW" />

          {/* ---------- I18N + BREADTH ---------- */}
          <Feature icon={Globe} title="Bilingual — English & Spanish" body="Every module. Every column header. Every alert. Flip the switch in the top-right and the entire OS speaks Spanish — landing page, dashboards, invoices, sheets, help text. Unlock the entire LatAm detailing market in one click." tag="All plans · NEW" />
          <Feature icon={ClipboardList} title="90+ Core Business Modules" body="Daily Work Log, Chemical Inventory, Payroll, Taxes, Accounting Dashboard, Client Profiles, Financing, Gas & Mileage, and now full Personal Finance — the 32-sheet detailing operating system PLUS a household finance system, all pre-built." tag="Pro & up" />
        </div>
      </section>

      {/* ---------- WHAT'S NEW — latest platform additions ---------- */}
      <section id="whats-new" className="border-t border-white/5 scroll-mt-20" data-testid="landing-latest-showcase">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <div className="max-w-3xl mb-14">
            <span className="text-xs tracking-widest text-[var(--ld-royal-hover)] uppercase">Fresh off the line</span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold mt-2">The newest weapons in the OS</h2>
            <p className="text-slate-400 mt-3 text-sm md:text-base">Three systems just shipped — built from real detailer pain, not feature checklists.</p>
          </div>
          <div className="space-y-16">
            {/* Survival Mode */}
            <div className="grid md:grid-cols-2 gap-8 items-center" data-testid="whats-new-survival">
              <div className="rounded-2xl overflow-hidden border border-white/10 group">
                <img src={NEW_SURVIVAL_IMG} alt="Cash-Flow Survival Mode dashboard" loading="lazy"
                  className="w-full h-64 md:h-80 object-cover group-hover:scale-[1.02] transition-transform duration-700" />
              </div>
              <div>
                <span className="text-[10px] px-2 py-0.5 rounded-full border border-orange-500/40 bg-orange-500/10 text-orange-300 uppercase tracking-wider">New · Pro</span>
                <h3 className="font-heading text-2xl font-bold mt-3">Cash-Flow Survival Mode</h3>
                <p className="text-slate-400 text-sm mt-2">Four color-coded operating levels watch your personal + business cash, protected obligations and 90-day forecasts — and tell you exactly what to cut before cash hits zero.</p>
                <ul className="mt-4 space-y-2 text-sm text-slate-300">
                  <li className="flex gap-2"><Check size={15} className="text-emerald-400 shrink-0 mt-0.5" /> Bulk-deal guard — see if a "deal" would drop you a level before you buy</li>
                  <li className="flex gap-2"><Check size={15} className="text-emerald-400 shrink-0 mt-0.5" /> Buying advice shifts automatically: bulk → monthly → weekly → per-job</li>
                  <li className="flex gap-2"><Check size={15} className="text-emerald-400 shrink-0 mt-0.5" /> Wants analyzer + three one-tap recovery budgets and a Run Survival Plan checklist</li>
                </ul>
              </div>
            </div>
            {/* AI Avatar Videos */}
            <div className="grid md:grid-cols-2 gap-8 items-center" data-testid="whats-new-avatar">
              <div className="md:order-2 rounded-2xl overflow-hidden border border-white/10 group">
                <img src={NEW_AVATAR_IMG} alt="AI avatar video creator" loading="lazy"
                  className="w-full h-64 md:h-80 object-cover group-hover:scale-[1.02] transition-transform duration-700" />
              </div>
              <div className="md:order-1">
                <span className="text-[10px] px-2 py-0.5 rounded-full border border-sky-500/40 bg-sky-500/10 text-sky-300 uppercase tracking-wider">New · Academy</span>
                <h3 className="font-heading text-2xl font-bold mt-3">AI Videos That Star You</h3>
                <p className="text-slate-400 text-sm mt-2">Upload three photos and the AI Video Creator renders real MP4 lessons with your own avatar presenting every scene — same face, every video, powered by Veo 3.1.</p>
                <ul className="mt-4 space-y-2 text-sm text-slate-300">
                  <li className="flex gap-2"><Check size={15} className="text-emerald-400 shrink-0 mt-0.5" /> 8-second likeness test clip before you commit to a full render</li>
                  <li className="flex gap-2"><Check size={15} className="text-emerald-400 shrink-0 mt-0.5" /> Storyboard → stitched multi-scene video with narration, posters and download</li>
                  <li className="flex gap-2"><Check size={15} className="text-emerald-400 shrink-0 mt-0.5" /> Auto render queue — blocked renders start themselves when capacity clears</li>
                </ul>
              </div>
            </div>
            {/* Course Builder */}
            <div className="grid md:grid-cols-2 gap-8 items-center" data-testid="whats-new-academy">
              <div className="rounded-2xl overflow-hidden border border-white/10 group">
                <img src={NEW_ACADEMY_IMG} alt="No-code Academy course builder" loading="lazy"
                  className="w-full h-64 md:h-80 object-cover group-hover:scale-[1.02] transition-transform duration-700" />
              </div>
              <div>
                <span className="text-[10px] px-2 py-0.5 rounded-full border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 uppercase tracking-wider">New · Academy</span>
                <h3 className="font-heading text-2xl font-bold mt-3">No-Code Course Builder</h3>
                <p className="text-slate-400 text-sm mt-2">Turn your detailing knowledge into paid courses — curriculum builder, slideshows, multi-page lessons, quizzes, certificates and student analytics, with an AI co-writer on tap.</p>
                <ul className="mt-4 space-y-2 text-sm text-slate-300">
                  <li className="flex gap-2"><Check size={15} className="text-emerald-400 shrink-0 mt-0.5" /> AI lesson writer, quiz generator and slide-deck generator</li>
                  <li className="flex gap-2"><Check size={15} className="text-emerald-400 shrink-0 mt-0.5" /> Drop-off analytics flag the exact lesson where students stall</li>
                  <li className="flex gap-2"><Check size={15} className="text-emerald-400 shrink-0 mt-0.5" /> Coupon share links, course invitations and a student preview simulator</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* JOURNEY: Solo → Team → Multi-tier */}
      <section id="journey" className="relative border-t border-white/5">
        <div className="absolute inset-0 opacity-[0.06] pointer-events-none">
          <img src={BENTO_IMG} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 py-24">
          <div className="max-w-3xl mb-14">
            <span className="text-xs tracking-widest text-slate-500 uppercase">{t("landing.journey_eyebrow")}</span>
            <h2 className="mt-2 font-heading text-4xl md:text-5xl font-bold tracking-tight">
              {t("landing.journey_title_line1")}<br />
              <span className="text-[var(--ld-royal-hover)]">{t("landing.journey_title_line2")}</span>
            </h2>
            <p className="mt-4 text-slate-400 leading-relaxed">
              {t("landing.journey_body")}
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4">
            <Stage
              step="01" title="Solo Detailer"
              body="Track jobs, quote clients in 30 seconds, run mileage vs. fuel, and export IRS-ready reports. Free up to 20 jobs, then $8.99 Starter or $29.99 Pro."
              icon={User}
              tone="ghost"
            />
            <Stage
              step="02" title="You Hire a Rep"
              body="Upgrade to Premium ($59.99). Add reps, invite them, and each gets their own /rep portal. They log placements + sales; you approve."
              icon={UserCheck}
              tone="warm"
            />
            <Stage
              step="03" title="Multi-Tier Team"
              body="Reps recruit reps. Set your override % once. A self-scaling street team compounds your revenue with zero fixed payroll overhead."
              icon={Network}
              tone="hot"
            />
            <Stage
              step="04" title="Rental + Fleet Empire"
              body="Upgrade to Platinum ($99). Your existing tools become a semi-passive rental business AND your service vehicles get true cost-per-mile tracking — Trust Score gating, RTO agreements, auto-computed valuation, curved depreciation, and drive-fee math injected into every quote."
              icon={Diamond}
              tone="platinum"
            />
            <Stage
              step="05" title="Household on Autopilot"
              body="A whole 4th workspace: household budget, bills tracker, net-worth ledger, avalanche debt payoff, savings goals & Plaid-ready bank sync. Because scaling a business while your personal money is on fire is how detailers burn out. Included with Pro+."
              icon={PiggyBank}
              tone="royal"
            />
          </div>
        </div>
      </section>

      {/* ============================================================
           2) BUSINESS ACCOUNTING & BOOKKEEPING — PRD §5, §6
           3) AUTOMATION — PRD §17
           4) OS HUB RADIAL — PRD §21
           5) PF SEPARATION CALLOUT — PRD §16
           ============================================================ */}
      <AccountingAndProfitSection />
      <AutomationSection />
      <OsHubSection />
      <PfSeparationCallout />

      {/* COMPARISON — vs. QuickBooks + Excel + Jobber-style CRM */}
      <section className="max-w-7xl mx-auto px-6 py-24 border-t border-white/5">
        <div className="max-w-3xl mb-4">
          <span className="text-xs tracking-widest text-slate-500 uppercase">Why replace your stack</span>
          <h2 className="mt-2 font-heading text-4xl md:text-5xl font-bold tracking-tight">
            One tool instead of five.
          </h2>
          <p className="mt-4 text-slate-400 leading-relaxed">
            An honest look at what generic tools DO and DON&apos;T give a detailing business.
            Where competitors have partial support, we mark it — we&apos;re not going to pretend they have zero features.
          </p>
        </div>
        <div className="flex flex-wrap gap-3 text-[11px] text-slate-400 mb-6 mt-6">
          <span className="inline-flex items-center gap-1.5"><Check size={12} className="text-[var(--ld-profit)]" strokeWidth={2.5}/> Fully supported</span>
          <span className="inline-flex items-center gap-1.5"><span className="text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[var(--ld-warn)]/15 text-[var(--ld-warn)] border border-[var(--ld-warn)]/30">partial</span> Some support / manual work</span>
          <span className="inline-flex items-center gap-1.5"><span className="text-slate-600 text-lg leading-none">✕</span> Not supported</span>
        </div>
        <div className="ld-card overflow-x-auto">
          <table className="w-full min-w-[820px]">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left text-xs uppercase tracking-widest text-slate-500 p-4">Capability</th>
                <th className="text-center text-xs uppercase tracking-widest text-slate-500 p-4">QuickBooks<br/><span className="text-[9px] normal-case text-slate-600">$35+/mo</span></th>
                <th className="text-center text-xs uppercase tracking-widest text-slate-500 p-4">Excel / Sheets<br/><span className="text-[9px] normal-case text-slate-600">$0–13/mo</span></th>
                <th className="text-center text-xs uppercase tracking-widest text-slate-500 p-4">Field CRM<br/><span className="text-[9px] normal-case text-slate-600">Jobber-style · $49+/mo</span></th>
                <th className="text-center text-xs uppercase tracking-widest text-[var(--ld-royal-hover)] p-4">Legacy IO<br/><span className="text-[9px] normal-case text-[var(--ld-royal-hover)]">from $29.99</span></th>
              </tr>
            </thead>
            <tbody>
              {[
                // ============ Core Accounting ============
                { header: "🧾 Core Accounting" },
                ["Invoicing + expense tracking", true, "manual", true, true],
                ["Bank/CC reconciliation", true, "manual", "add-on", true],
                ["Estimated quarterly tax reports", true, "manual", false, true],
                ["Profit & Loss / balance sheet", true, "manual", "partial", true],
                ["1099 / W-2 payroll integration", "add-on", false, "add-on", true],
                // ============ Detailing-Specific ============
                { header: "🚗 Detailing-Specific Ops" },
                ["Chemical inventory + cost-per-job", false, "manual", false, true],
                ["Gas vs. IRS mileage optimizer", false, "manual", false, true],
                ["Quick-quote for exterior / interior / RV / boat", false, "manual", "partial", true],
                ["Service kit builder (bundled chemicals)", false, "manual", false, true],
                ["DI resin lifecycle tracker", false, false, false, true],
                ["Equipment depreciation schedule", "partial", "manual", false, true],
                // ============ Clients & Financing ============
                { header: "💳 Clients & Financing" },
                ["Client profiles + service history", false, "manual", true, true],
                ["Professional invoices (PDF + email + SMS)", false, "partial", "partial", true],
                ["Public pay-online link (Stripe checkout)", false, false, false, true],
                ["Overdue auto-reminders (configurable schedule)", false, "manual", false, true],
                ["Batch invoice CSV export for accounting", false, "manual", false, true],
                ["3/6/12-month in-house financing (with APR)", false, "manual", false, true],
                ["Automated late-fee + default tracker", "partial", "manual", false, true],
                ["Rolling-window rewards tiers (auto tier-down)", false, false, false, true],
                ["Financing Command Center (charts + KPIs)", false, "manual", false, true],
                ["Job Profit + Risk Calculator", false, "manual", false, true],
                ["Weekly booking + revenue forecast", false, "manual", true, true],
                // ============ Sales Team ============
                { header: "👥 Sales Team & Commissions" },
                ["Sales rep roster + roles", false, "manual", "partial", true],
                ["QR-tracked referral codes per rep", false, false, false, true],
                ["Multi-tier commission cascades (5 levels deep)", false, false, false, true],
                ["Financing origination commissions (tiered)", false, false, false, true],
                ["Isolated rep portal (mobile self-service)", false, false, "partial", true],
                ["Admin approval queue for rep-submitted sales", false, false, false, true],
                ["Anonymized store-category ROI insights for reps", false, false, false, true],
                ["Software affiliate program (15% recurring)", false, false, false, true],
                // ============ Vehicle Ops (Platinum) ============
                { header: "🚙 Vehicle Ops & Auto Maintenance (Platinum)" },
                ["Multi-vehicle fleet tracking", false, "manual", false, true],
                ["Editable maintenance schedule + work log", false, "manual", false, true],
                ["Curved depreciation model (13-tier age curve)", false, "manual", false, true],
                ["Rolling 10,000-mile reserve fund math", false, false, false, true],
                ["True cost-per-mile → injects into every quote", false, false, false, true],
                // ============ Tool Rental (Platinum) ============
                { header: "💠 Tool Rental Business (Platinum)" },
                ["Tool catalog + package builder", false, "manual", false, true],
                ["Rental order + auto late-fee engine", false, "manual", false, true],
                ["Legacy Trust Score™ (auto customer scoring)", false, false, false, true],
                ["Rent-to-Own agreements + PMT amortization", false, "manual", false, true],
                ["Live business valuation (multiplier method)", false, "manual", false, true],
                ["Marketing ROI + Capital Allocation (NPV)", false, "manual", false, true],
                // ============ Personal Finance (Pro+ NEW) ============
                { header: "💰 Personal Finance Workspace (Pro+ · NEW)" },
                ["Household budget with 50/30/20 framework", false, "manual", false, true],
                ["Monthly bills tracker w/ due-date alerts", false, "manual", false, true],
                ["Live net worth (assets − liabilities)", false, "manual", false, true],
                ["Avalanche debt payoff engine (interest saved)", false, "manual", false, true],
                ["Emergency fund + unlimited savings goals", false, "manual", false, true],
                ["Month-by-month payoff schedule chart", false, "manual", false, true],
                ["Plaid bank auto-sync (Plaid-ready schema)", false, false, false, "roadmap"],
                // ============ Platform ============
                { header: "⚡ Platform" },
                ["Built specifically for detailers", false, false, false, true],
                ["Bilingual English & Spanish (every screen)", false, false, false, true],
                ["Mobile-first rep app", false, false, "partial", true],
                ["Data export + 100% data ownership", true, true, "partial", true],
                ["14-day full-Platinum free trial (no card)", false, false, "partial", true],
              ].map((row, i) => {
                if (row.header) {
                  return (
                    <tr key={`h-${i}`} className="border-b border-white/5">
                      <td colSpan={5} className="px-4 pt-6 pb-2 text-[10px] uppercase tracking-widest text-slate-400 font-semibold">
                        {row.header}
                      </td>
                    </tr>
                  );
                }
                const [cap, qb, xl, crm, us] = row;
                return (
                  <tr key={cap} className="border-b border-white/5 last:border-0 hover:bg-white/5 transition-colors">
                    <td className="p-4 text-sm">{cap}</td>
                    <td className="p-4 text-center">{cell(qb)}</td>
                    <td className="p-4 text-center">{cell(xl)}</td>
                    <td className="p-4 text-center">{cell(crm)}</td>
                    <td className="p-4 text-center bg-[var(--ld-royal)]/5">{cell(us)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="text-[11px] text-slate-500 mt-4 leading-relaxed max-w-3xl">
          Based on publicly-listed features as of Feb 2026. &ldquo;Field CRM&rdquo; represents category leaders (Jobber, HouseCall Pro, Workiz).
          &ldquo;Manual&rdquo; means possible with spreadsheet formulas but requires you to build + maintain it yourself. &ldquo;Add-on&rdquo; means available only via a paid third-party integration.
          Comparison intended as a decision aid — verify current features on each vendor&apos;s pricing page before switching.
        </p>
      </section>

      {/* TOP REFERRERS LEADERBOARD (public, anonymized) */}
      <section id="leaderboard" className="max-w-7xl mx-auto px-6 py-24 border-t border-white/5 scroll-mt-20">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <span className="pill-royal">
            <Gift size={12} className="inline mr-1" /> Passive Income Leaderboard
          </span>
          <h2 className="mt-4 font-heading text-4xl md:text-5xl font-bold tracking-tight">
            Detailers earning while they sleep.
          </h2>
          <p className="mt-4 text-slate-400 leading-relaxed">
            Every partner refers Legacy IO to another detailer → earns <strong className="text-[var(--ld-royal-hover)]">15% recurring</strong>. Their reps sell it too → the chain compounds up to 5 tiers deep. Real anonymized totals below.
          </p>
        </div>

        {leaderboard && leaderboard.top.length > 0 ? (
          <>
            <div className="grid md:grid-cols-3 gap-4 max-w-3xl mx-auto mb-10">
              <SummaryTile label="Combined Lifetime Earned" value={fmtMoney(leaderboard.totals.combined_lifetime)} tone="green" testId="lb-lifetime" />
              <SummaryTile label="Partner Detailers" value={leaderboard.totals.partners} tone="blue" testId="lb-partners" />
              <SummaryTile label="Active Subscriptions" value={leaderboard.totals.active_subscriptions} tone="silver" testId="lb-active" />
            </div>
            <div className="max-w-4xl mx-auto space-y-2" data-testid="leaderboard-list">
              {leaderboard.top.map((row) => (
                <LeaderRow key={row.rank} row={row} />
              ))}
            </div>
            <div className="text-center mt-10">
              <button onClick={() => nav("/register")} className="ld-btn ld-btn-primary text-base" data-testid="leaderboard-cta">
                Join the leaderboard — start free <ArrowRight size={16} />
              </button>
              <div className="mt-2 text-[10px] text-slate-500 uppercase tracking-widest">
                Anonymized · Only public partner label + totals shown · Never reveals customer data
              </div>
            </div>
          </>
        ) : (
          <div className="ld-card p-12 text-center max-w-2xl mx-auto" data-testid="leaderboard-empty">
            <Crown size={32} className="mx-auto text-[var(--ld-warn)] mb-4" />
            <h3 className="font-heading text-2xl font-bold">Be one of the first partners.</h3>
            <p className="mt-3 text-slate-400 text-sm leading-relaxed">
              The leaderboard is fresh — early partners get a permanent head start on the recurring passive-income stream. Sign up, share your link, and watch this table fill with your name at the top.
            </p>
            <button onClick={() => nav("/register")} className="ld-btn ld-btn-primary mt-6" data-testid="leaderboard-empty-cta">
              Claim your referral link <ArrowRight size={16} />
            </button>
          </div>
        )}
      </section>

      {/* 50-STATE PAYROLL COVERAGE */}
      <section id="payroll-coverage" className="max-w-7xl mx-auto px-6 py-24 border-t border-white/5 scroll-mt-20">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <div className="text-[11px] uppercase tracking-widest text-emerald-300/80 mb-3" data-testid="coverage-eyebrow">
            Built-in payroll engine
          </div>
          <h2 className="font-heading text-lg md:text-lg font-bold" data-testid="coverage-heading">
            Payroll-ready in all 50 states
          </h2>
          <p className="text-sm text-slate-400 mt-3">
            Versioned 2026 withholding formulas and SUTA rules for every US state — from California's
            Method B exact calculation to Pennsylvania's flat rate. Calculation-only: it computes every
            liability and paystub, you stay in control of payments.
          </p>
        </div>
        <div className="ld-card p-6 max-w-5xl mx-auto" data-testid="landing-coverage-card">
          <PayrollCoverageMap />
        </div>
      </section>

      {/* PRICING (3 tiers) */}
      <section id="pricing" className="max-w-7xl mx-auto px-6 py-24 border-t border-white/5">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <span className="pill-royal">{t("landing.pricing_eyebrow")}</span>
          <h2 className="mt-4 font-heading text-4xl md:text-5xl font-bold tracking-tight">
            {t("landing.pricing_title")}
          </h2>
          <p className="mt-4 text-slate-400 leading-relaxed">
            {t("landing.pricing_body")}
          </p>

          {/* Monthly / Yearly toggle */}
          <div className="mt-8 inline-flex items-center gap-1 p-1 bg-[var(--ld-surface-2)] border border-white/10 rounded-full"
               data-testid="landing-billing-toggle">
            <button
              onClick={() => setBillingCycle("monthly")}
              className={`px-5 py-2 rounded-full text-sm font-medium transition ${billingCycle==='monthly' ? 'bg-white text-black' : 'text-slate-400 hover:text-white'}`}
              data-testid="landing-interval-monthly"
            >{t("landing.billing_monthly")}</button>
            <button
              onClick={() => setBillingCycle("yearly")}
              className={`px-5 py-2 rounded-full text-sm font-medium transition inline-flex items-center gap-2 ${billingCycle==='yearly' ? 'bg-white text-black' : 'text-slate-400 hover:text-white'}`}
              data-testid="landing-interval-yearly"
            >
              {t("landing.billing_yearly")}
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${billingCycle==='yearly' ? 'bg-[var(--ld-profit)]/20 text-[var(--ld-profit)]' : 'bg-[var(--ld-royal)]/20 text-[var(--ld-royal-hover)]'}`}>
                {t("landing.billing_save_pct", { pct: P.annual_discount_pct })}
              </span>
            </button>
          </div>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 max-w-7xl mx-auto">
          {/* STARTER — get-in-the-door tier */}
          <div className="ld-card p-8 relative" data-testid="landing-card-starter">
            <div className="flex items-baseline justify-between">
              <span className="chip inline-flex items-center gap-1.5" style={{ background: "linear-gradient(135deg, rgba(20,184,166,0.15), rgba(20,184,166,0.05))", borderColor: "rgba(20,184,166,0.4)", color: "#5EEAD4" }}>
                <Sparkles size={10} /> Starter
              </span>
              <span className="text-xs text-slate-500">Get in the door</span>
            </div>
            <div className="mt-5 flex items-baseline gap-2">
              <span className="font-heading text-5xl font-bold tabular-nums text-teal-300" data-testid="landing-price-starter">${priceOf("starter").toFixed(2)}</span>
              <span className="text-slate-400 text-sm">{suffix}</span>
            </div>
            <div className="mt-1 text-xs text-slate-500">{secondaryLine("starter")}</div>
            <SavingsPill isYearly={isYearly} savings={yearlySavings("starter")} tone="teal" />
            <button onClick={() => nav("/register")} className="ld-btn ld-btn-ghost w-full justify-center mt-6" data-testid="starter-signup-btn">
              Start with Starter <ArrowRight size={16} />
            </button>
            <ul className="mt-6 pt-6 border-t border-white/10 space-y-2.5 text-sm">
              <li className="uppercase text-[10px] tracking-widest font-semibold text-teal-300">Everything you need to log & drive:</li>
              {[
                "Log every job you'll ever run — no more artificial limits",
                "Reclaim thousands in mileage deductions the IRS actually accepts",
                "Quote on the spot and close before your competitor calls back",
                "Keep every customer's number and history in one searchable place",
                "Runs on your phone in the driveway — ditch the clipboard",
                "Month-to-month billing. Cancel anytime, no lock-in",
              ].map(l => (
                <li key={l} className="flex items-start gap-2 text-slate-300">
                  <Check size={14} className="text-teal-400 mt-0.5 shrink-0" /> {l}
                </li>
              ))}
            </ul>
            <div className="mt-5 pt-4 border-t border-white/5 text-xs text-slate-500">
              Ready for invoicing, financing, and full accounting? <span className="text-[var(--ld-royal-hover)]">Upgrade to Pro anytime</span>.
            </div>
          </div>

          {/* PRO */}
          <div className="ld-card p-8 relative">
            <div className="flex items-baseline justify-between">
              <span className="pill-royal">Pro</span>
              <span className="text-xs text-slate-500">Solo detailer</span>
            </div>
            <div className="mt-5 flex items-baseline gap-2">
              <span className="font-heading text-5xl font-bold tabular-nums" data-testid="landing-price-pro">${priceOf("pro").toFixed(2)}</span>
              <span className="text-slate-400 text-sm">{suffix}</span>
            </div>
            <div className="mt-1 text-xs text-slate-500">{secondaryLine("pro")}</div>
            <SavingsPill isYearly={isYearly} savings={yearlySavings("pro")} tone="royal" />
            <button onClick={() => nav("/register")} className="ld-btn ld-btn-ghost w-full justify-center mt-6" data-testid="pricing-signup-btn">
              Start free trial <ArrowRight size={16} />
            </button>
            <ul className="mt-6 pt-6 border-t border-white/10 space-y-2.5 text-sm">
              {[
                "NEW: Kill Mint & YNAB — one app runs your business AND your household finances",
                "NEW: See your exact debt-free date and how much interest you save — live",
                "Track unlimited jobs so no revenue slips through the cracks",
                "Replace 6 apps with one — quoting, invoicing, mileage, accounting, chemicals & more",
                "Get paid faster — invoices email, text, and Stripe-pay in one send",
                "Stop chasing overdue clients — reminders send themselves until they pay",
                "Hand your accountant everything at tax time in one CSV click",
                "Turn 'too expensive' into 'yes' with 3/6/12-month financing plans",
                "Know your true profit per job, per month, per quarter — instantly",
                "Save hours at tax time — deductions pre-categorized for Schedule C",
                "Never guess your true cost per job — chemicals tracked to the ounce",
                "Your data stays yours — export anywhere, anytime, with one click",
              ].map(l => (
                <li key={l} className="flex items-start gap-2 text-slate-300">
                  <Check size={16} className="text-[var(--ld-profit)] mt-0.5 shrink-0" /> {l}
                </li>
              ))}
            </ul>
          </div>

          {/* PREMIUM */}
          <div className="ld-card p-8 relative border-2 border-[var(--ld-royal-hover)]/40">
            <div className="absolute -top-3 left-8 px-3 py-1 rounded-full bg-[var(--ld-royal-hover)] text-black text-[10px] tracking-widest uppercase font-semibold">
              Most Popular
            </div>
            <div className="flex items-baseline justify-between">
              <span className="pill-royal">Premium</span>
              <span className="text-xs text-slate-500">Multi-rep shops</span>
            </div>
            <div className="mt-5 flex items-baseline gap-2">
              <span className="font-heading text-5xl font-bold tabular-nums text-[var(--ld-royal-hover)]" data-testid="landing-price-premium">${priceOf("premium").toFixed(2)}</span>
              <span className="text-slate-400 text-sm">{suffix}</span>
            </div>
            <div className="mt-1 text-xs text-slate-500">{secondaryLine("premium")}</div>
            <SavingsPill isYearly={isYearly} savings={yearlySavings("premium")} tone="royal" />
            <button onClick={() => nav("/register")} className="ld-btn ld-btn-primary w-full justify-center mt-6" data-testid="premium-signup-btn">
              Start Premium free trial <ArrowRight size={16} />
            </button>
            <ul className="mt-6 pt-6 border-t border-white/10 space-y-2.5 text-sm">
              <li className="text-[var(--ld-royal-hover)] uppercase text-[10px] tracking-widest font-semibold">Everything in Pro, plus:</li>
              {[
                "Grow beyond yourself — reps bring you jobs while you focus on detailing",
                "5-tier commission cascade runs itself — zero spreadsheets",
                "Reps earn on financed jobs too — new commission stream, tiered by loan size",
                "See profit AND risk on every quote before you agree to the price",
                "Financing Command Center flags overdue loans before they bleed you",
                "Loyalty tiers auto-reset when customers stop coming in — cashback stays honest",
                "Every rep gets a private portal — they see their numbers, never yours",
                "Approve rep sales in one tap — commissions cascade automatically",
                "Learn which store types actually convert — spend flyer money where it works",
                "Reps compete on a live leaderboard — motivation is free, revenue is yours",
                "Refer another detailer and earn 15% of their subscription — forever",
              ].map(l => (
                <li key={l} className="flex items-start gap-2 text-slate-300">
                  <Sparkles size={14} className="text-[var(--ld-royal-hover)] mt-1 shrink-0" /> {l}
                </li>
              ))}
            </ul>
          </div>

          {/* PLATINUM */}
          <div className="ld-card p-8 relative"
            style={{ borderColor: "rgba(232, 193, 60, 0.4)", boxShadow: "0 0 80px rgba(232, 193, 60, 0.12)" }}>
            <div className="absolute -top-3 left-8 px-3 py-1 rounded-full text-black text-[10px] tracking-widest uppercase font-semibold"
              style={{ background: "linear-gradient(135deg, #F59E0B, #FCD34D)" }}>
              <Diamond size={9} className="inline -mt-0.5 mr-1" /> Rental Empire
            </div>
            <div className="flex items-baseline justify-between">
              <span className="chip inline-flex items-center gap-1.5"
                style={{ background: "linear-gradient(135deg, rgba(232,193,60,0.15), rgba(232,193,60,0.05))", borderColor: "rgba(232,193,60,0.4)", color: "#FCD34D" }}>
                <Diamond size={10} /> Platinum
              </span>
              <span className="text-xs text-slate-500">Semi-passive income</span>
            </div>
            <div className="mt-5 flex items-baseline gap-2">
              <span className="font-heading text-5xl font-bold tabular-nums bg-gradient-to-r from-yellow-500 to-amber-300 bg-clip-text text-transparent" data-testid="landing-price-platinum">${priceOf("platinum").toFixed(2)}</span>
              <span className="text-slate-400 text-sm">{suffix}</span>
            </div>
            <div className="mt-1 text-xs text-slate-500">{secondaryLine("platinum")}</div>
            <SavingsPill isYearly={isYearly} savings={yearlySavings("platinum")} tone="gold" />
            <button onClick={() => nav("/register")} className="ld-btn ld-btn-primary w-full justify-center mt-6" data-testid="platinum-signup-btn"
              style={{ background: "linear-gradient(135deg, #B45309, #F59E0B)" }}>
              Start Platinum free trial <ArrowRight size={16} />
            </button>
            <ul className="mt-6 pt-6 border-t border-white/10 space-y-2.5 text-sm">
              <li className="uppercase text-[10px] tracking-widest font-semibold" style={{ color: "#FCD34D" }}>Everything in Premium, plus:</li>
              {[
                "Turn your idle tools into a second business — rent what's already in your garage",
                "Build rental packages in minutes — customers pick, you get paid",
                "Late returns pay for themselves — automated fees, no awkward calls",
                "Sniff out risky renters before they cost you a tool — Trust Score does the vetting",
                "40% of every rental credits toward a purchase — customers keep coming back",
                "Track every service vehicle — repair + depreciation, always current",
                "Charge accurately for driving — true cost-per-mile injected into every quote",
                "Reserve 10k-miles ahead — the next big repair funds itself, monthly",
                "Never guess which upgrade pays back — NPV ranks every purchase decision",
                "Know what your business is worth today — for lending, partners, or exit",
              ].map(l => (
                <li key={l} className="flex items-start gap-2 text-slate-300">
                  <Diamond size={13} className="text-yellow-400 mt-1 shrink-0" /> {l}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="max-w-4xl mx-auto px-6 py-24 border-t border-white/5">
        <div className="max-w-2xl mb-10">
          <span className="text-xs tracking-widest text-slate-500 uppercase">Straight answers</span>
          <h2 className="mt-2 font-heading text-4xl md:text-5xl font-bold tracking-tight">Frequently asked</h2>
        </div>
        <div className="space-y-3">
          <FaqItem q="How does the new Client Invoice system work?" a="Every Pro-tier account gets a full invoicing suite. Turn a completed job (or several) into a client-facing invoice in one click — line items auto-derive from the Daily Work Log. Send it via email (Resend), SMS (Twilio), or copy the public share link. The customer opens a branded /invoice/{token} page with a 'Pay Now' Stripe button — funds land in your Stripe. Download a print-ready PDF for their records. Overdue reminders fire on your configured cadence (default 3, 7, 14 days after due date), respect a max-reminders cap, and de-dupe within 24 hours. Batch CSV export for your accountant is one click away." />
          <FaqItem q="What is the new Personal Finance workspace?" a="A complete household finance operating system living as the 4th workspace tab in your Legacy IO account — included with any Pro plan or above. Track monthly income + expenses in a 50/30/20 budget with live actuals from a daily log, forecast bills with due-date alerts, ledger every bank account and asset for a real-time net worth, and use the avalanche debt-payoff engine to see the exact month you'll be debt-free (plus how much interest you save vs. paying minimums forever). Emergency fund auto-computes from your essentials budget × months of coverage. Every transaction is stored in a Plaid-compatible schema so when we ship bank auto-sync you flip a switch and your feed goes live instantly. Detailers who scale past $500k/yr almost always have their personal money on fire — this fixes it." />
          <FaqItem q="Why bundle personal finance with a detailing business tool?" a="Because they're the same problem. A detailer running a healthy $30k/mo business but $80k in personal credit-card debt with no emergency fund is still one bad month from selling the business. We built Legacy IO to make detailers ACTUALLY wealthy — not just busy. The Personal Finance workspace was already a $497 Etsy spreadsheet template we admired; we rebuilt it inside your existing login so there's one truth about your money." />
          <FaqItem q="Is the platform bilingual?" a="Yes. Every screen — landing page, dashboards, all 90+ modules, invoices, help text, error messages — is fully translated to Spanish. Flip the language switcher in the top-right; the entire OS follows. Auto-detects your browser language on first load. If you're the only English-speaking OS in a Spanish-speaking market, that's a moat." />
          <FaqItem q="What is Platinum / Tool Rental Pro?" a="Platinum ($99/mo) unlocks two big things: an 18-module semi-passive tool-rental business and a full Auto Maintenance Management module. Tool Rental turns your polishers, extractors, and trailers into a second revenue stream with a Tool Catalog, Package Builder, Rental Orders with automated late-fee escalation, Legacy Trust Score™ customer scoring, Rent-to-Own agreements with PMT amortization, and a Financial Dashboard with live business valuation. Auto Maintenance tracks every service vehicle — editable schedule, work log, curved depreciation, and a rolling 10k-mile reserve fund — then injects a true cost-per-mile into Local Routes, Cross-City Planner, and Quick Quote so you finally charge accurately for driving." />
          <FaqItem q="How does the new Job Profit + Risk Calculator work?" a="It runs live on every quote. Enter the price, down payment, financing plan, and hard costs — it shows your net profit AND the risk you're carrying (down payment vs. financing fees). If a job looks profitable on paper but the customer only put 10% down, the calculator flags it. Embedded in Quick Quote, the Financing Hub, and the standalone /app/job-risk page." />
          <FaqItem q="What are Financing Origination Commissions?" a="A new commission stream in Premium. When your rep gets a customer approved on 3/6/12-month in-house financing, they earn — tiered by loan size (configurable ranges + percentages). The commission cascades up the 5-tier recruiter chain just like every other sale, and shows in the rep's earnings breakdown." />
          <FaqItem q="Why did you change the Rewards program?" a="The old lifetime-spend model meant a customer who spent big two years ago is 'Platinum' forever — even if they haven't been back in a year. We rebuilt it as a rolling-window tier system: tiers recompute every job from a configurable 3-month spend window (change it to 1/6/12 months in Admin config). Customers actually move down when they stop coming in, so cashback rates stay honest and rewards keep working as a retention lever." />
          <FaqItem q="How does the software referral program work?" a="Every account gets a personal referral link. When a detailer signs up through it and stays subscribed, you earn 15% of their monthly bill — for as long as they're active. Your reps can share their own tracked version of the link; if they close the sale, they get a cut of your 15% (you set the %, 10–30%). And overrides cascade 5 tiers deep for their recruiter chain. It's true passive income." />
          <FaqItem q="How do multi-tier commissions actually work?" a="You set a single override % (e.g. 5%). When your rep recruits another rep (and you approve them), your rep earns 5% of every commission the sub-rep closes. That cascades up to 5 tiers. It's automatic — every approval triggers the compute + cascade." />
          <FaqItem q="Can my reps see my business data?" a="No. Reps only see their own portal at /rep — their KPIs, bonus progress, and anonymized org-wide store ROI. They cannot see customer names, other reps' sales, or your accounting. Card placements they made? Yes. The bookings that came from them? Only you." />
          <FaqItem q="What happens after 20 free jobs?" a="You're prompted to start a 14-day trial of Pro, Premium, or Platinum. Your data stays. If you don't upgrade, we lock new-job entry (existing data remains read-only). Come back anytime." />
          <FaqItem q="What's included in the 14-day free trial?" a="Everything. Full Platinum access — every module, every feature, every calculator. So you can see the multi-tier commission engine AND the tool-rental system in action before you decide which tier fits your business." />
          <FaqItem q="Do you replace QuickBooks?" a="For a detailing business, yes. Legacy handles income, expenses, deductions, financed AR, payroll, quarterly estimated tax, and (on Platinum) full P&L auto-generated from rental activity. If you have a CPA who insists on QuickBooks, you can still export from Legacy monthly." />
          <FaqItem q="Is this mobile-friendly?" a="Yes. The rep portal is designed mobile-first — your team can log a sale or drop cards from their phone in under 30 seconds. The main dashboard works on any tablet or laptop." />
          <FaqItem q="Can I delete a rep login without losing their commission history?" a="Yes. The invite queue lets you delete a login (removes access) while keeping the sales_reps row + all commission history intact. Re-invite anytime and they pick up where they left off." />
        </div>
      </section>

      {/* CLOSING CTA */}
      <section className="max-w-7xl mx-auto px-6 py-24 border-t border-white/5">
        <div className="ld-card p-12 md:p-16 relative overflow-hidden">
          <div className="absolute inset-0 hero-grad opacity-40 pointer-events-none" />
          <div className="relative max-w-2xl">
            <span className="pill-royal">{t("landing.cta_ready_pill", { defaultValue: "Ready when you are" })}</span>
            <h2 className="mt-4 font-heading text-4xl md:text-5xl font-bold tracking-tight">
              {t("landing.cta_ready_title")}
            </h2>
            <p className="mt-4 text-slate-300 leading-relaxed">
              {t("landing.cta_ready_body")}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <button onClick={() => nav("/register")} className="ld-btn ld-btn-primary text-base" data-testid="closing-cta">
                {t("landing.cta_start_free_trial")} <ArrowRight size={16} />
              </button>
              <Link to="/pricing" className="ld-btn ld-btn-ghost" data-testid="closing-pricing">{t("landing.cta_see_pricing")}</Link>
            </div>
          </div>
        </div>
      </section>

      <footer className="max-w-7xl mx-auto px-6 py-10 border-t border-white/5 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-slate-500">
        <div className="flex items-center gap-3">
          <div className="w-6 h-6 rounded-md bg-[var(--ld-royal)] flex items-center justify-center font-heading font-bold text-[10px]">L</div>
          <div>© {new Date().getFullYear()} Legacy Detailing IO · Built by detailers, for detailers</div>
        </div>
        <div className="flex gap-6">
          <a href="#features" className="hover:text-white transition-colors">Features</a>
          <a href="#pricing" className="hover:text-white transition-colors">Pricing</a>
          <a href="#faq" className="hover:text-white transition-colors">FAQ</a>
          <Link to="/privacy" className="hover:text-white transition-colors" data-testid="landing-footer-privacy">Privacy</Link>
          <Link to="/terms" className="hover:text-white transition-colors" data-testid="landing-footer-terms">Terms</Link>
          <Link to="/login" className="hover:text-white transition-colors">Log in</Link>
          <Link to="/rep/login" className="hover:text-cyan-300 transition-colors" data-testid="landing-footer-rep-login">Sales rep sign-in</Link>
        </div>
      </footer>

      {/* Floating AI chat assistant — answers visitor questions about the app */}
      <LandingChatWidget />
    </div>
  );
}

/* ---------- Sub-components ---------- */

function SavingsPill({ isYearly, savings, tone = "royal" }) {
  const { t } = useTranslation();
  if (!savings || savings <= 0) return null;
  const toneClass = isYearly
    ? "bg-[var(--ld-profit)]/15 text-[var(--ld-profit)] border-[var(--ld-profit)]/40"
    : tone === "teal"
    ? "bg-teal-500/10 text-teal-300 border-teal-500/30"
    : tone === "gold"
    ? "bg-yellow-500/10 text-yellow-300 border-yellow-500/30"
    : "bg-[var(--ld-royal)]/15 text-[var(--ld-royal-hover)] border-[var(--ld-royal)]/30";
  return (
    <div className={`mt-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-widest border ${toneClass}`}
         data-testid="savings-pill">
      {isYearly ? <Check size={11} strokeWidth={3} /> : <Sparkles size={10} />}
      {isYearly
        ? t("landing.you_save_per_year", { amount: savings.toFixed(2) })
        : t("landing.save_per_year", { amount: savings.toFixed(2) })}
    </div>
  );
}



function Stat({ label, value, tone }) {
  const color = tone === "green" ? "text-[var(--ld-profit)]"
    : tone === "blue" ? "text-[var(--ld-royal-hover)]"
    : "text-[var(--ld-silver-light)]";
  return (
    <div className="p-3 bg-[var(--ld-surface-2)] border border-white/5 rounded-md">
      <div className="text-[10px] tracking-widest text-slate-500 uppercase">{label}</div>
      <div className={`font-heading font-bold text-xl mt-1 tabular-nums ${color}`}>{value}</div>
    </div>
  );
}

function Feature({ icon: Icon, title, body, tag }) {
  return (
    <div className="ld-card p-6 group hover:border-[var(--ld-royal-hover)]/30 transition-colors">
      <div className="flex items-start justify-between">
        <div className="w-10 h-10 rounded-md bg-[var(--ld-royal)]/15 border border-[var(--ld-royal)]/30 flex items-center justify-center group-hover:bg-[var(--ld-royal)]/25 transition-colors">
          <Icon size={18} className="text-[var(--ld-royal-hover)]" strokeWidth={1.5} />
        </div>
        {tag && (
          <span className={`text-[9px] tracking-widest uppercase px-2 py-0.5 rounded-full font-semibold ${
            tag === "Premium"
              ? "bg-[var(--ld-royal-hover)]/15 text-[var(--ld-royal-hover)] border border-[var(--ld-royal-hover)]/30"
              : tag === "Platinum"
              ? "bg-yellow-400/10 text-yellow-300 border border-yellow-400/30"
              : "bg-white/5 text-slate-400 border border-white/10"
          }`}>{tag}</span>
        )}
      </div>
      <h3 className="mt-4 font-heading text-xl font-semibold">{title}</h3>
      <p className="mt-2 text-sm text-slate-400 leading-relaxed">{body}</p>
    </div>
  );
}


/* ─────── Field-first mini-tiles (v2.70+ modules) ─────── */
function FieldTile({ icon: Icon, title, body, tone }) {
  const toneMap = {
    royal:   { ring: "border-[var(--ld-royal-hover)]/40",  bg: "bg-[var(--ld-royal)]/10",  text: "text-[var(--ld-royal-hover)]" },
    cyan:    { ring: "border-cyan-500/40",                 bg: "bg-cyan-500/10",           text: "text-cyan-300" },
    emerald: { ring: "border-emerald-500/40",              bg: "bg-emerald-500/10",        text: "text-emerald-300" },
    amber:   { ring: "border-amber-500/40",                bg: "bg-amber-500/10",          text: "text-amber-300" },
    violet:  { ring: "border-violet-500/40",               bg: "bg-violet-500/10",         text: "text-violet-300" },
  };
  const t = toneMap[tone] || toneMap.royal;
  return (
    <div className={`ld-card p-5 border ${t.ring} hover:-translate-y-0.5 transition-transform duration-300`}>
      <div className={`w-10 h-10 rounded-md ${t.bg} border ${t.ring} flex items-center justify-center mb-4`}>
        <Icon size={18} className={t.text} strokeWidth={1.5} />
      </div>
      <div className="flex items-center gap-1.5 mb-1">
        <h3 className="font-heading text-base font-bold">{title}</h3>
        <span className="text-[8px] uppercase tracking-widest text-emerald-400/70 font-bold">NEW</span>
      </div>
      <p className="text-xs text-slate-400 leading-relaxed">{body}</p>
    </div>
  );
}

function MiniCallout({ icon: Icon, label }) {
  return (
    <div className="flex items-center gap-2 rounded-md border border-white/5 bg-white/[0.02] px-3 py-2.5 text-xs text-slate-300">
      <Icon size={13} className="text-emerald-400 flex-shrink-0" strokeWidth={1.75} />
      <span>{label}</span>
    </div>
  );
}

function Stage({ step, title, body, icon: Icon, tone }) {
  const ring = tone === "hot" ? "border-[var(--ld-royal-hover)]/40 bg-[var(--ld-royal)]/10"
    : tone === "warm" ? "border-[var(--ld-warn)]/30 bg-[var(--ld-warn)]/5"
    : tone === "platinum" ? "border-yellow-400/40 bg-yellow-400/5"
    : tone === "royal" ? "border-[var(--ld-royal)]/40 bg-[var(--ld-royal)]/[0.06]"
    : "border-white/10";
  const num = tone === "hot" ? "text-[var(--ld-royal-hover)]"
    : tone === "warm" ? "text-[var(--ld-warn)]"
    : tone === "platinum" ? "text-yellow-300"
    : tone === "royal" ? "text-[var(--ld-royal-hover)]"
    : "text-slate-500";
  return (
    <div className={`ld-card p-6 border ${ring} transition-transform hover:-translate-y-1 duration-300`}>
      <div className="flex items-baseline justify-between">
        <span className={`font-heading text-3xl font-bold tabular-nums ${num}`}>{step}</span>
        <Icon size={20} className="text-slate-400" strokeWidth={1.5} />
      </div>
      <h3 className="mt-5 font-heading text-2xl font-semibold">{title}</h3>
      <p className="mt-3 text-sm text-slate-400 leading-relaxed">{body}</p>
    </div>
  );
}

function cell(v) {
  if (v === true) return <Check size={16} className="text-[var(--ld-profit)] mx-auto" strokeWidth={2.5} />;
  if (v === false) return <span className="text-slate-600 text-lg leading-none" title="Not supported">✕</span>;
  if (typeof v === "string") {
    // "partial", "manual", "add-on", "$$", etc. — render as amber pill
    return <span className="inline-block text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[var(--ld-warn)]/15 text-[var(--ld-warn)] border border-[var(--ld-warn)]/30 whitespace-nowrap">{v}</span>;
  }
  return null;
}

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="ld-card overflow-hidden" data-testid="faq-item">
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between p-5 text-left hover:bg-white/[0.02] transition-colors"
        data-testid="faq-toggle"
      >
        <span className="font-semibold text-sm md:text-base pr-4">{q}</span>
        <ChevronDown size={18} className={`shrink-0 text-slate-400 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      <div className={`grid transition-all duration-300 ${open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}>
        <div className="overflow-hidden">
          <div className="px-5 pb-5 text-sm text-slate-400 leading-relaxed">{a}</div>
        </div>
      </div>
    </div>
  );
}

function fmtMoney(n) {
  const v = Number(n || 0);
  if (v >= 1000) return `$${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  return `$${v.toFixed(2)}`;
}

function SummaryTile({ label, value, tone, testId }) {
  const color = tone === "green" ? "text-[var(--ld-profit)]"
    : tone === "blue" ? "text-[var(--ld-royal-hover)]"
    : "text-[var(--ld-silver-light)]";
  return (
    <div className="ld-card p-5 text-center" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`font-heading text-3xl md:text-4xl font-bold mt-2 tabular-nums ${color}`}>{value}</div>
    </div>
  );
}

function LeaderRow({ row }) {
  const rankStyle = row.rank === 1 ? { icon: <Crown size={16} />, cls: "bg-[var(--ld-warn)]/15 text-[var(--ld-warn)] border-[var(--ld-warn)]/40" }
    : row.rank === 2 ? { icon: <Medal size={16} />, cls: "bg-slate-300/10 text-slate-300 border-slate-300/30" }
    : row.rank === 3 ? { icon: <Medal size={16} />, cls: "bg-orange-500/10 text-orange-400 border-orange-500/30" }
    : { icon: <span className="font-heading font-bold text-xs">{row.rank}</span>, cls: "bg-white/5 text-slate-400 border-white/10" };
  return (
    <div
      className="ld-card px-5 py-4 flex items-center justify-between gap-4 flex-wrap hover:border-[var(--ld-royal-hover)]/30 transition-colors"
      data-testid={`leader-row-${row.rank}`}
    >
      <div className="flex items-center gap-4 min-w-0">
        <div className={`w-10 h-10 shrink-0 rounded-full flex items-center justify-center border ${rankStyle.cls}`}>
          {rankStyle.icon}
        </div>
        <div className="min-w-0">
          <div className="font-heading font-semibold text-lg truncate">{row.partner_label}</div>
          <div className="text-[10px] text-slate-500 uppercase tracking-widest mt-0.5">
            {row.active_subscribers} sub · {row.reps_selling} rep{row.reps_selling !== 1 ? "s" : ""} selling · {row.months_active} mo active
          </div>
        </div>
      </div>
      <div className="text-right">
        <div className="text-[10px] uppercase tracking-widest text-slate-500">Lifetime</div>
        <div className="font-heading font-bold text-2xl text-[var(--ld-profit)] tabular-nums">
          {fmtMoney(row.lifetime_earnings)}
        </div>
      </div>
    </div>
  );
}


// ============================================================================
// PRD §2 — Connected Workflow Strip
// ============================================================================
function ConnectedWorkflowStrip() {
  const steps = [
    { icon: MessageCircle, label: "Lead",       tone: "text-cyan-300" },
    { icon: Users,         label: "Customer",   tone: "text-sky-300"  },
    { icon: FileText,      label: "Quote",      tone: "text-blue-300" },
    { icon: ClipboardList, label: "Job",        tone: "text-violet-300" },
    { icon: CreditCard,    label: "Payment",    tone: "text-fuchsia-300" },
    { icon: Receipt,       label: "Expenses",   tone: "text-amber-300" },
    { icon: TrendingUp,    label: "Profit",     tone: "text-emerald-300" },
    { icon: MailCheck,     label: "Follow-Up",  tone: "text-rose-300" },
  ];
  return (
    <section className="border-t border-white/5" data-testid="landing-workflow-strip">
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-16">
        <div className="max-w-3xl mb-8">
          <span className="text-xs tracking-widest text-slate-500 uppercase">One connected system</span>
          <h2 className="mt-2 font-heading text-2xl md:text-3xl font-bold tracking-tight">
            Every workflow feeds the next — <span className="text-[var(--ld-royal-hover)]">no more stitching five tools together</span>.
          </h2>
          <p className="mt-3 text-sm text-slate-400 leading-relaxed">
            Information flows automatically from lead capture to follow-up. Complete the job once — customers, jobs, inventory, payments and books all update in the same breath.
          </p>
        </div>
        <div className="flex flex-nowrap items-center overflow-x-auto pb-2 gap-2 md:gap-3 md:justify-between">
          {steps.map((s, i) => (
            <div key={s.label} className="flex items-center gap-2 md:gap-3 shrink-0">
              <div className="ld-card px-3 md:px-4 py-2.5 md:py-3 border border-white/10 flex flex-col items-center min-w-[92px] md:min-w-[112px]">
                <s.icon size={18} className={s.tone} />
                <div className="text-[11px] md:text-xs mt-1.5 font-semibold text-slate-100 whitespace-nowrap">{s.label}</div>
              </div>
              {i < steps.length - 1 && <ArrowRight size={14} className="text-slate-600 shrink-0" />}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============================================================================
// PRD §5 & §6 — Business Accounting & Bookkeeping + Job Profitability sample
// ============================================================================
function AccountingAndProfitSection() {
  const capabilities = [
    { icon: DollarSign,   label: "Revenue tracking" },
    { icon: Receipt,      label: "Expense tracking" },
    { icon: TrendingUp,   label: "Profit & Loss" },
    { icon: LineChart,    label: "Cash flow" },
    { icon: FileText,     label: "Accounts receivable" },
    { icon: BookOpen,     label: "Accounts payable" },
    { icon: Package,      label: "Inventory accounting" },
    { icon: Coins,        label: "Commission expenses" },
    { icon: CreditCard,   label: "Stripe fees" },
    { icon: Landmark,     label: "Business debt & interest" },
    { icon: Wallet,       label: "Owner contributions & draws" },
    { icon: PiggyBank,    label: "Tax reserve estimates" },
  ];
  return (
    <section id="accounting" className="border-t border-white/5 scroll-mt-20" data-testid="landing-accounting-section">
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-20 md:py-24">
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-7">
            <span className="text-xs tracking-widest text-emerald-400/80 uppercase">Your operations build your books</span>
            <h2 className="mt-2 font-heading text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight">
              Complete the job once. <br />
              <span className="text-[var(--ld-royal-hover)]">The financial records update automatically.</span>
            </h2>
            <p className="mt-4 text-slate-400 leading-relaxed max-w-2xl">
              A full bookkeeping layer that reads directly from your jobs, inventory, payments and payroll — no double entry, no month-end scramble. Chart of accounts, double-entry-compatible ledger, monthly close, tax package, and accountant portal all included.
            </p>
            <div className="mt-8 grid grid-cols-2 md:grid-cols-3 gap-3">
              {capabilities.map((c) => (
                <div key={c.label}
                     className="ld-card p-3 border border-white/5 flex items-center gap-2"
                     data-testid={`landing-accounting-${c.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  <c.icon size={14} className="text-cyan-300 shrink-0" />
                  <span className="text-[12px] text-slate-200">{c.label}</span>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-slate-500 mt-4 flex items-start gap-1.5" data-testid="landing-accounting-disclaimer">
              <Info size={11} className="shrink-0 mt-0.5" />
              Financial reports and tax estimates are provided for business management purposes and are not tax or accounting advice.
            </p>
          </div>

          {/* Job Profitability sample card — PRD §6 */}
          <div className="lg:col-span-5">
            <div className="ld-card p-5 border border-emerald-500/25" data-testid="landing-profit-card">
              <div className="text-[10px] uppercase tracking-widest text-emerald-300">Job Profitability · demo</div>
              <div className="text-sm text-slate-200 mt-1">Interior Deep Clean · 2020 F-150</div>
              <div className="mt-4 space-y-1.5 text-sm">
                <ProfitRow label="Service revenue" value="$179.00" tone="text-emerald-300" bold />
                <ProfitRow label="− Chemicals" value="−$8.42" tone="text-slate-300" />
                <ProfitRow label="− DI water (7.2 gal @ $0.31)" value="−$2.23" tone="text-slate-300" />
                <ProfitRow label="− Resin consumption" value="−$3.10" tone="text-slate-300" />
                <ProfitRow label="− Travel (18 mi × $0.68)" value="−$12.24" tone="text-slate-300" />
                <ProfitRow label="− Payment processing (2.9%)" value="−$5.19" tone="text-slate-300" />
                <ProfitRow label="− Sales commission" value="−$17.90" tone="text-slate-300" />
                <ProfitRow label="− Business interest allocation" value="−$2.11" tone="text-slate-500" />
                <div className="border-t border-white/10 pt-2 mt-2">
                  <ProfitRow label="Actual job profit" value="$127.81" tone="text-emerald-300" bold />
                  <div className="text-[11px] text-slate-500 mt-0.5">71.4% margin · example data</div>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-white/5 text-[11px] text-slate-500">
                Every direct cost is pulled from the actual work log, DI tank, chemical inventory, mileage log and fee schedule.
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ProfitRow({ label, value, tone, bold }) {
  return (
    <div className="flex items-center justify-between">
      <span className={`${tone} ${bold ? "font-semibold" : ""}`}>{label}</span>
      <span className={`tabular-nums ${tone} ${bold ? "font-semibold" : ""}`}>{value}</span>
    </div>
  );
}

// ============================================================================
// PRD §17 — Automation section
// ============================================================================
function AutomationSection() {
  const flows = [
    { from: "Customer fills intake",    to: "CRM updates",              icon: Users },
    { from: "Customer accepts quote",   to: "Calendar updates",         icon: Calendar },
    { from: "Job completed",            to: "Inventory updates",        icon: Package },
    { from: "DI resin updates",         to: "Job costs update",         icon: Droplet },
    { from: "Payment received",         to: "Accounting updates",       icon: CreditCard },
    { from: "Commission earned",        to: "Sales dashboard updates",  icon: Coins },
    { from: "Follow-up due",            to: "Customer SMS/email fires", icon: BellRing },
  ];
  return (
    <section id="automation" className="border-t border-white/5 scroll-mt-20" data-testid="landing-automation-section">
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-20 md:py-24">
        <div className="max-w-3xl mb-10">
          <span className="text-xs tracking-widest text-violet-300/80 uppercase">Automation</span>
          <h2 className="mt-2 font-heading text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight">
            Run the business without <span className="text-[var(--ld-royal-hover)]">entering the same data five times</span>.
          </h2>
          <p className="mt-3 text-slate-400 leading-relaxed">
            Each action inside the OS auto-updates every downstream system. Fewer spreadsheets, fewer sync errors, fewer late nights reconciling.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {flows.map((f) => (
            <div key={f.from}
                 className="ld-card p-4 border border-white/5"
                 data-testid={`landing-flow-${f.from.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-slate-500">
                <f.icon size={12} className="text-cyan-300" /> Auto-flow
              </div>
              <div className="mt-2 text-sm text-slate-200 font-medium">{f.from}</div>
              <div className="flex items-center gap-1.5 mt-1 text-[13px] text-emerald-300">
                <ArrowRight size={12} /> {f.to}
              </div>
            </div>
          ))}
        </div>
        <div className="mt-6 flex items-start gap-1.5 text-[11px] text-slate-500 max-w-2xl" data-testid="landing-ai-disclaimer">
          <Bot size={11} className="shrink-0 mt-0.5 text-cyan-300" />
          AI-powered assistance is clearly labelled in-app. Human review is available on any AI-generated output.
        </div>
      </div>
    </section>
  );
}

// ============================================================================
// PRD §21 — OS Hub Radial (12 modules orbit around the Detailing IO core)
// ============================================================================
function OsHubSection() {
  const modules = [
    { label: "CRM",             icon: Users,         tone: "text-cyan-300" },
    { label: "Scheduling",      icon: Calendar,      tone: "text-sky-300" },
    { label: "Jobs",            icon: ClipboardList, tone: "text-violet-300" },
    { label: "Inventory",       icon: Package,       tone: "text-emerald-300" },
    { label: "DI Water",        icon: Droplet,       tone: "text-cyan-300" },
    { label: "Contracts",       icon: FileText,      tone: "text-blue-300" },
    { label: "Sales",           icon: TrendingUp,    tone: "text-rose-300" },
    { label: "Payments",        icon: CreditCard,    tone: "text-fuchsia-300" },
    { label: "Accounting",      icon: BookOpen,      tone: "text-amber-300" },
    { label: "Analytics",       icon: LineChart,     tone: "text-emerald-300" },
    { label: "Customer Portal", icon: UserCheck,     tone: "text-sky-300" },
    { label: "AI",              icon: Sparkles,      tone: "text-yellow-300" },
  ];
  return (
    <section className="border-t border-white/5" data-testid="landing-os-hub">
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-20 md:py-24">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <span className="text-xs tracking-widest text-slate-500 uppercase">One connected operating system</span>
          <h2 className="mt-2 font-heading text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight">
            Every module, <span className="text-[var(--ld-royal-hover)]">one connected core</span>.
          </h2>
          <p className="mt-3 text-slate-400 leading-relaxed">
            Twelve deeply integrated modules. Every action touches only what it should — never the same data entered twice.
          </p>
        </div>

        {/* Radial hub — desktop. Mobile falls back to a clean grid. */}
        <div className="hidden md:block relative mx-auto" style={{ width: 620, height: 620 }}>
          <div className="absolute inset-16 rounded-full border border-white/5 bg-gradient-to-br from-cyan-500/5 to-violet-500/5" />
          <div className="absolute inset-28 rounded-full border border-white/5" />
          <div className="absolute inset-40 rounded-full border border-white/5" />

          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 ld-card p-6 border border-cyan-500/40 bg-gradient-to-br from-cyan-500/10 to-violet-500/10 text-center shadow-2xl shadow-cyan-500/20 w-52"
               data-testid="landing-os-hub-core">
            <Cpu size={22} className="text-cyan-300 mx-auto" />
            <div className="text-xs uppercase tracking-widest text-slate-400 mt-2">Detailing</div>
            <div className="font-heading font-bold text-lg text-white">Operating System</div>
          </div>

          {modules.map((m, i) => {
            const angle = (i / modules.length) * 2 * Math.PI - Math.PI / 2;
            const r = 260;
            const x = Math.cos(angle) * r;
            const y = Math.sin(angle) * r;
            return (
              <div key={m.label}
                   className="absolute ld-card px-3 py-2 border border-white/10 hover:border-cyan-500/40 transition-colors flex items-center gap-2"
                   style={{
                     left: `calc(50% + ${x}px)`,
                     top: `calc(50% + ${y}px)`,
                     transform: "translate(-50%, -50%)",
                   }}
                   data-testid={`landing-os-hub-chip-${m.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <m.icon size={13} className={m.tone} />
                <span className="text-xs font-medium text-slate-100 whitespace-nowrap">{m.label}</span>
              </div>
            );
          })}
        </div>

        {/* Mobile fallback — 3-column grid */}
        <div className="md:hidden grid grid-cols-3 gap-2">
          <div className="col-span-3 ld-card p-4 border border-cyan-500/30 bg-gradient-to-br from-cyan-500/10 to-violet-500/10 text-center"
               data-testid="landing-os-hub-core-mobile">
            <Cpu size={18} className="text-cyan-300 mx-auto" />
            <div className="text-[10px] uppercase tracking-widest text-slate-400 mt-1">Detailing</div>
            <div className="font-heading font-bold text-sm text-white">Operating System</div>
          </div>
          {modules.map((m) => (
            <div key={m.label}
                 className="ld-card px-2 py-2 border border-white/10 flex items-center gap-1.5"
                 data-testid={`landing-os-hub-chip-mobile-${m.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <m.icon size={11} className={m.tone + " shrink-0"} />
              <span className="text-[11px] font-medium text-slate-100 truncate">{m.label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============================================================================
// PRD §16 — Business / Personal Finance separation clarifier
// ============================================================================
function PfSeparationCallout() {
  return (
    <section className="border-t border-white/5" data-testid="landing-pf-separation">
      <div className="max-w-4xl mx-auto px-4 md:px-6 py-14 md:py-16">
        <div className="ld-card p-5 md:p-6 border border-white/10 flex items-start gap-4 flex-wrap md:flex-nowrap">
          <div className="shrink-0 w-10 h-10 rounded-lg bg-cyan-500/10 border border-cyan-500/25 flex items-center justify-center">
            <Briefcase size={18} className="text-cyan-300" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-heading font-semibold text-lg">Business and personal finance stay separate.</h3>
            <p className="mt-1.5 text-sm text-slate-400 leading-relaxed">
              Run your business books cleanly, then get optional visibility into your personal picture — household budget, net worth, debt payoff — inside the same login. Business and personal transactions are never automatically combined.
            </p>
            <p className="text-[11px] text-slate-500 mt-2 flex items-start gap-1.5">
              <Info size={11} className="shrink-0 mt-0.5" />
              Personal Finance is optional and can be excluded from any calculations you choose.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
